import { useDataManagementContext } from "@/components/ui/data-management/data-management-context";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { DataManagement, ViewMode } from "@faro-lotv/data-management";
import { LoadingScreen, grey } from "@faro-lotv/flat-ui";
import { selectCompanyId } from "@faro-lotv/project-source";
import { Box } from "@mui/material";
import type { JSX } from "react";
import { useParams } from "react-router-dom";

interface Props {
  /** View mode of the Data Management editor, provided by DataPreparationUi. */
  viewMode?: ViewMode;
  /** Setter for the view mode, provided by DataPreparationUi. */
  setViewMode?: React.Dispatch<React.SetStateAction<ViewMode>>;
  /**
   * Dev-only: hide WIZARD/ERROR temporarily to inspect poses without cancelling progress.
   * Provided by DataPreparationUi so that DevToolsForDraftView can control it.
   */
  isInspectForced?: boolean;
  /** Setter for isInspectForced, provided by DataPreparationUi. */
  setIsInspectForced?: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * @returns A wrapper around the Data Management editor provided by the @faro-lotv/data-management
 * library.
 */
export function DataManagementEditor({
  viewMode,
  setViewMode,
  isInspectForced,
  setIsInspectForced,
}: Props): JSX.Element | null {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  const { projectId } = useParams();

  const shouldBeHidden = viewMode === "INITIAL" || viewMode === "INSPECT";

  // File infos collected by the Data Management picker/dropzone.
  const { scanFileInfos, setScanFileInfos } = useDataManagementContext();

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        // Causes the header bar to stay fixed when scrolling on long pages.
        overflow: "auto",
        backgroundColor: grey[50],
        // The Data Management editor is always rendered so that it stays
        // up-to-date, but hidden with "display: none" when its view mode is "INSPECT".
        display: shouldBeHidden ? "none" : undefined,
      }}
    >
      {currentUser && companyId && projectId ? (
        <DataManagement
          currentUser={currentUser}
          companyId={companyId}
          projectId={projectId}
          fileInfos={scanFileInfos}
          setFileInfos={setScanFileInfos}
          setViewMode={setViewMode}
          isInspectForced={isInspectForced}
          setIsInspectForced={setIsInspectForced}
        />
      ) : (
        <LoadingScreen />
      )}
    </Box>
  );
}
